"use client";
import Link from "next/link";
import { useRouter } from "next13-progressbar";
import { type FormEventHandler, useEffect, useState } from "react";
import { GoogleIcon } from "@/components/Icons";
import { Button } from "@/components/ui/design-system/button";
import { Input } from "@/components/ui/design-system/input";
import { AuthenticationStep } from "@/lib/analytics/segmentEvents";
import { trackAuthFlow } from "@/lib/analytics/trackEvents";
import { formatOAuthDiscoveryStartURL } from "@/lib/StytchLib";
import { toast } from "./ui/use-toast";

const isValidEmail = (emailValue: string) => {
  const regex = /\S+@\S+\.\S+/;
  return regex.test(emailValue);
};

type PasswordLoginFormProps = {
  organizationId: string;
  dontRedirect?: boolean;
  onSubmit: (
    organizationId: string,
    email: string,
    password: string,
    // biome-ignore lint/suspicious/noConfusingVoidType: placeholder explanation
  ) => Promise<void | { status_code: number }>;
  onReset?: (
    email: string,
  ) => Promise<
    { status_code: number } | { status_code: number; message: string }
  >;
};

export const PasswordLoginForm = ({
  organizationId,
  onSubmit,
  onReset,
  dontRedirect,
}: PasswordLoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsDisabled(!isValidEmail(email) || password.length < 6);
  }, [email, password]);

  const onSubmitFormHandler: FormEventHandler = async (e) => {
    e.preventDefault();

    if (isDisabled) {
      return;
    }

    setIsLoading(true);

    try {
      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "login attempt",
          type: "password",
          status: "submitted",
          email: email,
          emailDomain: email.split("@")[1],
          organizationId,
        },
      });

      await onSubmit(organizationId, email, password);

      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "login attempt",
          type: "password",
          status: "success",
          email: email,
          emailDomain: email.split("@")[1],
          organizationId,
        },
      });

      if (!dontRedirect) {
        toast({
          title: "Login successful",
          description: "Redirecting you to the dashboard.",
        });
        router.push("/dashboard");
      }
    } catch (error) {
      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "login attempt",
          type: "password",
          status: "failed",
          email: email,
          emailDomain: email.split("@")[1],
          organizationId,
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });

      toast({
        title: "Login failed",
        description: "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onResetHandler = async () => {
    if (!email || !isValidEmail(email)) {
      toast({
        title: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (onReset) {
      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "password reset",
          status: "requested",
          email: email,
          emailDomain: email.split("@")[1],
        },
      });

      const resp = await onReset(email);
      if (resp.status_code === 200) {
        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "password reset",
            status: "email sent",
            email: email,
            emailDomain: email.split("@")[1],
          },
        });

        toast({
          title: "Password reset email sent",
          description: "Please check your email for a reset link.",
        });
      } else {
        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "password reset",
            status: "failed",
            email: email,
            emailDomain: email.split("@")[1],
            errorCode: resp.status_code,
            errorMessage: "message" in resp ? resp.message : "Unknown error",
          },
        });

        toast({
          title: "Failed to reset password",
          description: `Please try again (${"message" in resp ? resp.message : "Unknown error"}).`,
          variant: "destructive",
        });
      }
    }
  };

  const handleGoogleLogin = () => {
    trackAuthFlow({
      step: AuthenticationStep.DISCOVERY_PAGE,
      additionalParams: {
        event: "login attempt",
        type: "google_oauth",
        status: "initiated",
      },
    });
  };

  const hostDomain =
    typeof window !== "undefined" ? window.location.origin : "";
  const googleAuthUrl = formatOAuthDiscoveryStartURL(hostDomain, "google");

  return (
    <div className="flex flex-col w-full gap-nf-ds-6">
      <Button variant="pill-outline" asChild className="w-full gap-nf-ds-3">
        <Link href={googleAuthUrl} onClick={handleGoogleLogin}>
          <GoogleIcon className="w-[18px] h-[18px] shrink-0" />
          <span>Continue with Google</span>
        </Link>
      </Button>

      <div className="relative flex items-center justify-center">
        <div className="grow h-px bg-nf-ds-gray-300/60" />
        <span className="px-nf-ds-4 font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-500">
          or
        </span>
        <div className="grow h-px bg-nf-ds-gray-300/60" />
      </div>

      <form
        onSubmit={onSubmitFormHandler}
        className="flex flex-col gap-nf-ds-5"
      >
        <div className="flex flex-col gap-nf-ds-2">
          <label
            htmlFor="email"
            className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-600"
          >
            Email
          </label>
          <Input
            id="email"
            name="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            className="h-12 rounded-nf-ds-2xl bg-nf-ds-white border-nf-ds-gray-200 px-nf-ds-5 shadow-nf-ds-xs"
          />
        </div>

        <div className="flex flex-col gap-nf-ds-2">
          <label
            htmlFor="password"
            className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-600"
          >
            Password
          </label>
          <Input
            id="password"
            name="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            className="h-12 rounded-nf-ds-2xl bg-nf-ds-white border-nf-ds-gray-200 px-nf-ds-5 shadow-nf-ds-xs"
          />
          {onReset && (
            <button
              type="button"
              onClick={onResetHandler}
              className="self-end font-nf-ds-sans text-[13px] text-nf-ds-gray-600 hover:text-nf-ds-gray-800 underline underline-offset-2"
            >
              Forgot password?
            </button>
          )}
        </div>

        <Button
          type="submit"
          variant="pill-primary"
          disabled={isDisabled || isLoading}
          className="w-full mt-nf-ds-2"
        >
          {isLoading ? (
            "Signing in…"
          ) : (
            <span className="inline-flex items-center gap-nf-ds-2">
              Sign in
              <span aria-hidden>→</span>
            </span>
          )}
        </Button>
      </form>
    </div>
  );
};
